import { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import UserDashboardLayout from "../../layouts/UserDashboardLayout";
import {
  FiSunrise, FiSun, FiMoon, FiCoffee, FiDroplet,
  FiCheck, FiLock, FiDownload, FiSliders,
  FiTrendingUp, FiAward, FiInfo, FiPlus,
  FiX, FiArrowRight,
  FiZap, FiFramer, FiChevronDown, FiCheckCircle, FiAlertTriangle
} from "react-icons/fi";
import { GiLotus } from "react-icons/gi";
import Topbar from "../../components/userDashboard/Topbar";
import BrandLoader from "../../components/BrandLoader";
import { useCheckIn } from "../../context/CheckInContext";
import { dietPlanAPI, userAPI } from "../../services/api";

const DAYS      = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const FULL_DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

// ─── BRANDED PDF / REPORT DOWNLOAD HELPER ────────────────────────────────────
const downloadDietPlanPDF = (dbPlan, userPref) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const DAYS_LIST = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
  const dietType = userPref?.dietType || "Veg";
  const region = userPref?.region || "Indian";
  const goal = userPref?.calorieGoal || "Balanced Wellness";

  let mealsHtml = '';
  DAYS_LIST.forEach(day => {
    const dayMeals = dbPlan?.meals?.filter(m => m.day === day) || [];
    mealsHtml += `
      <div style="margin-bottom: 24px; background: #FFFDF8; border: 1px solid #EFE5D8; border-radius: 16px; padding: 18px; box-shadow: 0 4px 12px rgba(0,0,0,0.03);">
        <h3 style="margin: 0 0 12px 0; color: #6B8A5A; font-family: 'Poppins', sans-serif; font-size: 16px; border-bottom: 2px solid #6B8A5A; padding-bottom: 6px;">
          ${day.toUpperCase()} MEAL SCHEDULE (${dietType} · ${region})
        </h3>
        <table style="width: 100%; border-collapse: collapse; font-family: 'Inter', sans-serif; font-size: 13px;">
          <thead>
            <tr style="background: rgba(107,138,90,0.1); color: #27500A; text-align: left;">
              <th style="padding: 8px 12px; border-radius: 6px 0 0 6px;">Time / Slot</th>
              <th style="padding: 8px 12px;">Meal Name</th>
              <th style="padding: 8px 12px;">Calories</th>
              <th style="padding: 8px 12px;">Macros (P / C / F)</th>
              <th style="padding: 8px 12px; border-radius: 0 6px 6px 0;">Notes / Tags</th>
            </tr>
          </thead>
          <tbody>
            ${dayMeals.length > 0 ? dayMeals.map(m => `
              <tr style="border-bottom: 1px solid #F1E8DA;">
                <td style="padding: 10px 12px; font-weight: bold; color: #F29A0C;">[${m.time || '08:00 AM'}]<br/><span style="font-size: 11px; color: #888;">${m.mealSlot}</span></td>
                <td style="padding: 10px 12px; font-weight: 700; color: #1F1F1F;">${m.mealName}</td>
                <td style="padding: 10px 12px; font-weight: bold; color: #E97818;">${m.calories} kcal</td>
                <td style="padding: 10px 12px; color: #555;">P: <b>${m.protein}g</b> | C: <b>${m.carbs}g</b> | F: <b>${m.fat}g</b></td>
                <td style="padding: 10px 12px; font-style: italic; color: #666; font-size: 12px;">${m.notes || m.tags || '-'}</td>
              </tr>
            `).join('') : `<tr><td colspan="5" style="padding: 12px; color: #888; text-align: center;">No meals scheduled for ${day}</td></tr>`}
          </tbody>
        </table>
      </div>
    `;
  });

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Divyyog Personalized Nutrition Plan</title>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@700;800;900&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        body { font-family: 'Inter', sans-serif; background: #FAF7F2; color: #1F1F1F; margin: 0; padding: 24px; }
        .page-container { background: #FFFFFF; border: 3px solid #6B8A5A; border-radius: 20px; padding: 36px; box-shadow: 0 10px 30px rgba(0,0,0,0.05); min-height: 94vh; box-sizing: border-box; position: relative; }
        .header { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid #F29A0C; padding-bottom: 20px; margin-bottom: 28px; }
        .brand-title { font-family: 'Poppins', sans-serif; font-size: 22px; font-weight: 900; color: #6B8A5A; letter-spacing: -0.5px; margin: 0; }
        .brand-sub { font-size: 11px; font-weight: 700; color: #F29A0C; text-transform: uppercase; letter-spacing: 1px; margin-top: 2px; }
        .badge { background: linear-gradient(135deg, #F29A0C, #E97818); color: white; padding: 7px 18px; border-radius: 20px; font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; }
        .meta-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 32px; }
        .meta-card { background: #FFFDF8; border: 1px solid #EFE5D8; border-radius: 14px; padding: 14px; text-align: center; }
        .meta-title { font-size: 10px; font-weight: 800; color: #888; text-transform: uppercase; letter-spacing: 0.8px; }
        .meta-val { font-family: 'Poppins', sans-serif; font-size: 15px; font-weight: 900; color: #1F1F1F; margin-top: 4px; }
        .footer { margin-top: 40px; border-top: 2px solid #EFE5D8; padding-top: 20px; display: flex; align-items: center; justify-content: space-between; font-size: 11px; color: #666; }
      </style>
    </head>
    <body>
      <div class="page-container">
        <div class="header">
          <div>
            <h1 class="brand-title">DIVYYOG FOUNDATION</h1>
            <p class="brand-sub">NUTRITION & HOLISTIC WELLNESS</p>
          </div>
          <div style="text-align: right;">
            <span class="badge">${dietType} Nutrition</span>
            <p style="margin: 6px 0 0 0; font-size: 11px; font-weight: 700; color: #6B8A5A;">Customized Report</p>
          </div>
        </div>

        <div style="margin-bottom: 24px;">
          <h2 style="font-family: 'Poppins', sans-serif; font-size: 24px; margin: 0 0 6px 0; color: #1F1F1F;">Personalized 7-Day Menu</h2>
          <p style="color: #666; font-size: 13px; margin: 0;">Tailored for ${dietType} · ${region} Cuisine · ${goal}</p>
        </div>

        <div class="meta-grid">
          <div class="meta-card"><div class="meta-title">Diet Choice</div><div class="meta-val" style="color:#6B8A5A;">${dietType}</div></div>
          <div class="meta-card"><div class="meta-title">Cuisine Region</div><div class="meta-val" style="color:#F29A0C;">${region}</div></div>
          <div class="meta-card"><div class="meta-title">Fitness Goal</div><div class="meta-val" style="color:#E97818;">${goal}</div></div>
          <div class="meta-card"><div class="meta-title">Water Target</div><div class="meta-val" style="color:#534AB7;">3.0 Liters / day</div></div>
        </div>

        ${mealsHtml}

        <div class="footer">
          <div>
            <p style="font-weight: 800; color: #6B8A5A; margin: 0 0 3px 0; font-family: 'Poppins', sans-serif;">DIVYYOG FOUNDATION</p>
            <p style="margin: 0; color: #888; font-size: 11px;">Empowering Mind, Body & Holistic Balance</p>
          </div>
          <div style="text-align: right;">
            <p style="margin: 0; color: #888;">www.divyayoga.org</p>
          </div>
        </div>
      </div>

      <script>
        window.onload = function() { window.print(); }
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(htmlContent);
  printWindow.document.close();
};

// ─── CUSTOM DROPDOWN SELECTOR ────────────────────────────────────────────────
const CustomSelect = ({ value, onChange, options, placeholder="Select...", label }) => {
  const [open, setOpen] = useState(false);
  const selectedObj = options.find(o => (typeof o === "string" ? o : o.value) === value);
  const displayLabel = typeof selectedObj === "string" ? selectedObj : selectedObj?.label || selectedObj?.value || value;

  return (
    <div className="relative w-full">
      {label && <label className="text-[10px] font-black uppercase tracking-[0.9px] block mb-1.5" style={{ color: "#999" }}>{label}</label>}
      <div 
        onClick={() => setOpen(!open)}
        className="w-full border rounded-[14px] px-3.5 py-2.5 text-[12px] font-bold flex items-center justify-between cursor-pointer transition-all"
        style={{
          borderColor: open ? "#6B8A5A" : "rgba(229,231,235,0.8)",
          background: "rgba(249,250,251,0.95)",
          color: "#1F1F1F",
          boxShadow: open ? "0 4px 14px rgba(107,138,90,0.15)" : "none"
        }}
      >
        <span>{displayLabel || placeholder}</span>
        <FiChevronDown size={14} style={{ color: "#999", transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}/>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div 
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 4, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 z-[300] max-h-52 overflow-y-auto rounded-[14px] p-1.5 shadow-2xl border"
            style={{ background: "#FFFDF8", borderColor: "rgba(239,229,216,0.9)", scrollbarWidth: "none" }}
          >
            {options.map((opt) => {
              const val = typeof opt === "string" ? opt : opt.value;
              const lbl = typeof opt === "string" ? opt : opt.label;
              const isSelected = val === value;
              return (
                <div
                  key={val}
                  onClick={() => { onChange(val); setOpen(false); }}
                  className="px-3 py-2 text-[12px] font-medium rounded-[10px] cursor-pointer flex items-center justify-between transition-colors mb-0.5"
                  style={{
                    background: isSelected ? "rgba(107,138,90,0.12)" : "transparent",
                    color: isSelected ? "#6B8A5A" : "#1F1F1F",
                    fontWeight: isSelected ? "700" : "500"
                  }}
                >
                  <span>{lbl}</span>
                  {isSelected && <FiCheckCircle size={13} style={{ color: "#6B8A5A" }}/>}
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── TOKENS & STYLES ─────────────────────────────────────────────────────────
const T = {
  orange: "#F29A0C", orange2: "#E97818",
  olive: "#6B8A5A",  olive2: "#8BA47A",
  dark: "#1F1F1F",   mid: "#555",
  muted: "#999",     light: "#CCC",
  border: "rgba(239,229,216,0.8)",
};

const glass = {
  background: "rgba(255,255,255,0.82)",
  border: "1px solid rgba(255,255,255,0.95)",
  backdropFilter: "blur(32px)",
  WebkitBackdropFilter: "blur(32px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.06), inset 0 1px 0 #fff",
};

const getTierRank = (tierName) => {
  const norm = (tierName || "").toLowerCase();
  if (norm.includes("premium") || norm.includes("pro") || norm.includes("ytt")) return 3;
  if (norm.includes("standard")) return 2;
  if (norm.includes("basic")) return 1;
  return 0;
};

const getMealChronologicalScore = (m) => {
  if (m?.time) {
    const match = String(m.time).match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (match) {
      let hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      const ampm = match[3].toUpperCase();
      if (ampm === "PM" && hours < 12) hours += 12;
      if (ampm === "AM" && hours === 12) hours = 0;
      return hours * 60 + minutes;
    }
  }
  const slotLower = (m?.mealSlot || "").toLowerCase();
  if (slotLower.includes("early") || slotLower.includes("pre")) return 100;
  if (slotLower.includes("breakfast")) return 300;
  if (slotLower.includes("lunch")) return 600;
  if (slotLower.includes("snack")) return 900;
  if (slotLower.includes("dinner")) return 1200;
  return 1500;
};

// ─── TILT CARD ────────────────────────────────────────────────────────────────
function TiltCard({ children, style = {}, className = "", intensity = 6 }) {
  const ref = useRef(null);
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [intensity, -intensity]), { stiffness: 260, damping: 28 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-intensity, intensity]), { stiffness: 260, damping: 28 });
  return (
    <motion.div ref={ref}
      style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d", perspective: 900, ...style }}
      className={className}
      onMouseMove={e => { const r = ref.current.getBoundingClientRect(); mx.set((e.clientX - r.left) / r.width - 0.5); my.set((e.clientY - r.top) / r.height - 0.5); }}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
    >{children}</motion.div>
  );
}

// ─── MACRO RING ───────────────────────────────────────────────────────────────
function MacroRing({ consumed = 0, target = 1850, outerLabel = "kcal", completedMeals = 0, totalMeals = 0 }) {
  const circ1 = 2 * Math.PI * 58;
  const calRatio = (target && target > 0) ? Math.min(consumed / target, 1) : 0;
  const offset1 = circ1 - calRatio * circ1;

  const circ2 = 2 * Math.PI * 42;
  const mealRatio = (totalMeals && totalMeals > 0) ? Math.min(completedMeals / totalMeals, 1) : 0;
  const offset2 = circ2 - mealRatio * circ2;

  const mealPct = Math.round(mealRatio * 100);

  return (
    <div style={{ position: "relative", width: 140, height: 140, margin: "0 auto" }}>
      <svg viewBox="0 0 140 140" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", transform: "rotate(-90deg)" }}>
        <circle cx="70" cy="70" r="58" stroke="#F1E8DA" strokeWidth="10" fill="none" />
        <motion.circle cx="70" cy="70" r="58" stroke="url(#mg1)" strokeWidth="10" fill="none" strokeLinecap="round"
          strokeDasharray={circ1} initial={{ strokeDashoffset: circ1 }} animate={{ strokeDashoffset: offset1 }}
          transition={{ duration: 1.4, delay: 0.2, ease: "easeOut" }} />
        <circle cx="70" cy="70" r="42" stroke="#ECF5E8" strokeWidth="8" fill="none" />
        <motion.circle cx="70" cy="70" r="42" stroke="url(#mg2)" strokeWidth="8" fill="none" strokeLinecap="round"
          strokeDasharray={circ2} initial={{ strokeDashoffset: circ2 }} animate={{ strokeDashoffset: offset2 }}
          transition={{ duration: 1.4, delay: 0.4, ease: "easeOut" }} />
        <defs>
          <linearGradient id="mg1" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="#F29A0C" /><stop offset="100%" stopColor="#E97818" /></linearGradient>
          <linearGradient id="mg2" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="#6B8A5A" /><stop offset="100%" stopColor="#8BA47A" /></linearGradient>
        </defs>
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <p className="font-black text-[#1F1F1F] leading-none" style={{ fontFamily: "'Poppins',sans-serif", fontSize: 24, letterSpacing: "-0.5px" }}>{consumed}</p>
        <p className="text-[10px] font-medium mt-0.5" style={{ color: T.muted }}>/ {target} {outerLabel}</p>
        <p className="text-[9px] font-bold mt-1 px-2 py-0.5 rounded-full" style={{ background: "rgba(107,138,90,0.12)", color: T.olive }}>
          {mealPct}% meals
        </p>
      </div>
    </div>
  );
}

// ─── PROGRESS BAR ─────────────────────────────────────────────────────────────
function ProgressBar({ value, max, color, track }) {
  const pct = Math.min(Math.round((value / (max || 1)) * 100), 100);
  return (
    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: track }}>
      <motion.div className="h-full rounded-full" style={{ background: color }}
        initial={{ width: 0 }} animate={{ width: `${pct}%` }}
        transition={{ duration: 1.1, ease: "easeOut" }} />
    </div>
  );
}

// ─── MEAL CARD ────────────────────────────────────────────────────────────────
function MealCard({ slot, meal, checked, onToggle, onUpgrade, userTierRank = 3, delay = 0 }) {
  const isLocked = meal.locked && userTierRank < 3;
  const Icon = slot.icon;

  const handleToggle = () => {
    onToggle();
  };

  if (isLocked) {
    return (
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
        whileHover={{ y: -3, borderColor: "rgba(242,154,12,0.4)" }}
        onClick={() => onUpgrade(slot.label)}
        className="rounded-[22px] p-5 flex items-center gap-5 cursor-pointer transition-all"
        style={{ background: "rgba(239,229,216,0.15)", border: "2px dashed rgba(239,229,216,0.6)" }}>
        <div className="w-12 h-12 rounded-[16px] flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(200,200,200,0.2)" }}>
          <FiLock size={20} style={{ color: T.light }} />
        </div>
        <div className="flex-1">
          <p className="text-[13px] font-bold" style={{ color: T.light }}>{slot.label} · {slot.time}</p>
          <p className="text-[12px] mt-0.5" style={{ color: T.light }}>Unlock with Premium to see this meal</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full font-bold text-[12px] text-white"
          style={{ background: `linear-gradient(135deg,${T.orange},${T.orange2})`, boxShadow: "0 6px 18px rgba(242,154,12,0.38)", whiteSpace: "nowrap" }}>
          Upgrade to Premium
        </div>
      </motion.div>
    );
  }

  const slotBg = slot?.bg || "rgba(255,244,232,0.75)";
  const slotAccent = slot?.accent || T.orange;
  const slotBorder = slot?.border || "rgba(242,154,12,0.18)";

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      whileHover={{ y: -5, boxShadow: `0 20px 50px ${slotAccent}25` }}
      className="relative rounded-[22px] p-5 overflow-hidden cursor-default"
      style={{
        background: checked ? (typeof slotBg === "string" ? slotBg.replace("0.75", "1") : slotBg) : slotBg,
        border: `1px solid ${checked ? slotAccent + "35" : slotBorder}`,
        boxShadow: checked ? `0 8px 24px ${slotAccent}20` : "0 8px 24px rgba(0,0,0,0.04)",
        transition: "all 0.3s cubic-bezier(0.25,1,0.5,1)",
      }}>
      <div className="flex items-start gap-4 relative">
        <motion.div
          animate={checked ? { scale: [1, 1.15, 1] } : {}}
          transition={{ duration: 0.4 }}
          className="w-11 h-11 rounded-[14px] flex items-center justify-center flex-shrink-0"
          style={checked
            ? { background: `linear-gradient(135deg,${slot.accent},${slot.accent}CC)`, boxShadow: `0 6px 16px ${slot.accent}45` }
            : { background: "rgba(255,255,255,0.8)" }
          }>
          <Icon size={18} style={{ color: checked ? "#fff" : slot.accent }} />
        </motion.div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.8px]" style={{ color: slot.accent }}>{slot.label}</span>
            <span className="text-[10px]" style={{ color: T.light }}>·</span>
            <span className="text-[10px]" style={{ color: T.light }}>{slot.time}</span>
            {meal.prep && <span className="text-[10px]" style={{ color: T.light }}>· {meal.prep} prep</span>}
          </div>
          <h3 className="font-black text-[15px] text-[#1F1F1F] mb-2.5 leading-tight"
            style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.3px",
              textDecoration: checked ? "line-through" : "none",
              opacity: checked ? 0.65 : 1 }}>
            {meal.name}
          </h3>
          {(meal.description || meal.notes) && (
            <p className="text-[12px] text-zinc-600 mb-2.5 leading-relaxed font-medium"
              style={{ textDecoration: checked ? "line-through" : "none", opacity: checked ? 0.6 : 0.9 }}>
              {meal.description || meal.notes}
            </p>
          )}

          <div className="flex flex-wrap gap-1.5 mb-2">
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1"
              style={{ background: "rgba(242,154,12,0.1)", color: "#854F0B" }}>
              <FiZap size={9} /> {meal.cal} kcal
            </span>
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1"
              style={{ background: "rgba(107,138,90,0.1)", color: "#27500A" }}>
              {meal.protein}g Protein
            </span>
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1"
              style={{ background: "rgba(242,154,12,0.08)", color: "#633806" }}>
              {meal.carbs}g Carbs
            </span>
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1"
              style={{ background: "rgba(83,74,183,0.08)", color: "#3C3489" }}>
              {meal.fat}g Fat
            </span>
            {meal.tags?.map(t => (
              <span key={t} className="text-[10px] font-semibold px-2.5 py-1 rounded-full"
                style={{ background: "rgba(200,200,200,0.15)", color: "#888" }}>{t}</span>
            ))}
          </div>

          {meal.notes && (
            <p className="text-[11px] leading-[1.5] italic flex items-start gap-1.5" style={{ color: T.muted }}>
              <FiInfo size={10} style={{ flexShrink: 0, marginTop: 2 }} />{meal.notes}
            </p>
          )}
        </div>

        <motion.button
          whileTap={{ scale: 0.85 }}
          onClick={handleToggle}
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 outline-none cursor-pointer"
          style={checked
            ? { background: `linear-gradient(135deg,${T.olive},${T.olive2})`, borderColor: T.olive, boxShadow: `0 4px 14px ${T.olive}45` }
            : { background: "rgba(255,255,255,0.8)", borderColor: "rgba(239,229,216,0.9)" }
          }
          aria-label={checked ? "Mark incomplete" : "Mark complete"}>
          <AnimatePresence>
            {checked && (
              <motion.div initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0 }} transition={{ type: "spring", stiffness: 400, damping: 20 }}>
                <FiCheck size={13} style={{ color: "#fff" }} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </div>
    </motion.div>
  );
}

// ─── UPGRADE MODAL ────────────────────────────────────────────────────────────
function UpgradeModal({ mealLabel, title, description, onClose }) {
  const heading = title || (mealLabel ? "Premium Required" : "Upgrade Required");
  const bodyText = description || (mealLabel ? `The ${mealLabel} meal plan is available for Premium members. Upgrade to unlock your complete personalised nutrition plan.` : "Upgrade your plan to unlock full AI diet generation, custom meal plans, and video library.");

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(14px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale: 0.85, y: 24 }} animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.85 }} transition={{ type: "spring", stiffness: 320, damping: 28 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-md rounded-[28px] overflow-hidden"
        style={{ boxShadow: "0 40px 100px rgba(0,0,0,0.45)" }}>
        <div className="relative p-8 text-white"
          style={{ background: `linear-gradient(145deg,${T.orange},${T.orange2})` }}>
          <button onClick={onClose} className="absolute top-5 right-5 w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.2)", border: "none", cursor: "pointer", color: "#fff" }}>
            <FiX size={15} />
          </button>
          <div className="w-14 h-14 rounded-[18px] flex items-center justify-center mb-5"
            style={{ background: "rgba(255,255,255,0.2)" }}>
            <FiAward size={24} style={{ color: "#fff" }} />
          </div>
          <h2 className="font-black text-[22px] mb-2" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>
            {heading}
          </h2>
          <p className="text-white/80 text-[13px] leading-[1.7]">
            {bodyText}
          </p>
        </div>
        <div style={{ background: "#fff", padding: "24px 28px" }}>
          <p className="text-[11px] font-bold uppercase tracking-[1px] mb-4" style={{ color: T.light }}>Premium diet plan unlocks</p>
          {[
            "Full 7-day personalised AI meal plan",
            "Unlimited custom cuisine & goal generations",
            "Region-specific & seasonal recipes",
            "Macro-adjusted for your yoga intensity",
            "Downloadable PDF meal plan",
            "Direct trainer nutrition consultation",
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 mb-3">
              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: `linear-gradient(135deg,${T.orange},${T.orange2})` }}>
                <FiCheck size={10} color="#fff" />
              </div>
              <span className="text-[13px]" style={{ color: T.mid }}>{item}</span>
            </div>
          ))}
          <div className="flex gap-3 mt-6">
            <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
              onClick={() => { onClose(); window.location.href = "/user/settings?tab=subscription"; }}
              className="flex-1 h-12 rounded-full font-bold text-[14px] text-white flex items-center justify-center gap-2"
              style={{ background: `linear-gradient(135deg,${T.orange},${T.orange2})`, boxShadow: "0 10px 28px rgba(242,154,12,0.38)", border: "none", cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              Upgrade Now <FiArrowRight size={14} />
            </motion.button>
            <button onClick={onClose} className="h-12 px-5 rounded-full font-semibold text-[13px]"
              style={{ background: "rgba(200,200,200,0.12)", border: `1px solid ${T.border}`, color: T.muted, cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              Later
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── QUOTA EXCEEDED MODAL ─────────────────────────────────────────────────────
function QuotaModal({ quotaInfo, onRedeemPoints, onClose }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/60 backdrop-blur-md"
      onClick={onClose}>
      <motion.div initial={{ scale: 0.88, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.88 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-[32px] overflow-hidden shadow-2xl border border-amber-100">
        <div className="p-7 text-white" style={{ background: `linear-gradient(135deg, ${T.orange}, ${T.orange2})` }}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
              <FiZap size={22} className="text-white"/>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center border-none cursor-pointer">
              <FiX size={15}/>
            </button>
          </div>
          <h3 className="text-xl font-black font-['Poppins']">Monthly AI Quota Reached</h3>
          <p className="text-white/90 text-xs mt-1.5 leading-relaxed">
            You've utilized your monthly allocation of AI diet plan generations.
          </p>
        </div>
        <div className="p-7 space-y-4">
          <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold text-amber-800 uppercase tracking-wider">Your Wellness Points</p>
              <p className="text-lg font-black text-amber-950 font-['Poppins']">{quotaInfo?.userPoints ?? 0} Pts</p>
            </div>
            <span className="text-[11px] font-semibold text-amber-700 bg-white px-3 py-1 rounded-full border border-amber-200">
              100 Pts = 1 Bonus Plan
            </span>
          </div>

          <p className="text-xs text-zinc-600 leading-relaxed">
            You can redeem 100 of your practice & attendance points to generate this plan right now, or wait until next month's refresh.
          </p>
          <div className="flex gap-2.5 pt-2">
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
              onClick={onRedeemPoints}
              disabled={(quotaInfo?.userPoints ?? 0) < 100}
              className={`flex-1 h-12 rounded-full text-white font-bold text-xs shadow-lg border-none cursor-pointer flex items-center justify-center gap-1.5 ${(quotaInfo?.userPoints ?? 0) >= 100 ? 'bg-gradient-to-r from-emerald-600 to-teal-600 shadow-emerald-600/30' : 'bg-gray-400 cursor-not-allowed'}`}>
              <FiCheckCircle size={14}/> Redeem 100 Points & Generate
            </motion.button>
            <button onClick={onClose}
              className="px-5 h-12 rounded-full border border-zinc-200 text-xs font-semibold text-zinc-600 bg-zinc-50 hover:bg-zinc-100 cursor-pointer">
              Later
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── DAY COMPLETE MODAL ───────────────────────────────────────────────────────
function DayCompleteModal({ dayName, onClose }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(12px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale: 0.85, y: 24 }} animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.85 }} transition={{ type: "spring", stiffness: 320, damping: 28 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-sm rounded-[28px] overflow-hidden"
        style={{ boxShadow: "0 40px 100px rgba(0,0,0,0.4)" }}>
        <div className="relative p-8 text-center"
          style={{ background: `linear-gradient(145deg,${T.olive},${T.olive2})` }}>
          <motion.div animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 2, repeat: Infinity }}
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ background: "rgba(255,255,255,0.2)" }}>
            <FiCheckCircle size={32} className="text-white" />
          </motion.div>
          <h3 className="text-2xl font-bold text-white mb-2 font-['Poppins']">Day Complete!</h3>
          <p className="text-sm text-white/80 mb-6">
            Awesome job finishing all meals for <span className="font-semibold text-white">{dayName}</span>!
          </p>
          <button onClick={onClose}
            className="w-full py-3 rounded-full font-bold text-sm bg-white text-zinc-800 hover:bg-zinc-100 cursor-pointer shadow-lg transition-all border-none">
            Continue Journey
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── HELPER: Clean Condition & Allergy Parser ────────────────────────────────
function parseConditionList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(s => String(s).trim()).filter(s => s && s.toLowerCase() !== "none" && s !== '["None"]');
  let str = String(raw).trim();
  if (str.startsWith("[") && str.endsWith("]")) {
    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) {
        return parsed.map(s => String(s).trim()).filter(s => s && s.toLowerCase() !== "none" && s !== '["None"]');
      }
    } catch (e) {}
  }
  return str.split(",")
    .map(s => s.replace(/[\[\]"']/g, "").trim())
    .filter(s => s && s.toLowerCase() !== "none" && s !== '["None"]');
}

const STANDARD_DIETS = ["Veg", "Non-Veg", "Vegan", "Eggetarian", "Jain", "Pescatarian", "Keto", "Sattvic"];
const STANDARD_REGIONS = ["Indian", "Asian", "Indian (North)", "Indian (South)", "Western", "Mediterranean", "Middle Eastern", "Global"];
const CALORIE_PRESETS = [1400, 1600, 1800, 2000, 2200];
const STANDARD_HEALTH_CONDITIONS = ["None", "Hypertension (BP)", "Diabetes", "Joint / Spine", "Anxiety / Stress", "PCOS / Thyroid", "Acidity / GERD"];
const STANDARD_ALLERGENS = ["None", "Dairy-Free", "Nut-Free", "Gluten-Free", "Soy-Free", "Egg-Free", "Shellfish-Free", "Low-Sodium"];
const STANDARD_ACTIVITY = ["Sedentary", "Lightly Active", "Moderately Active", "Very Active"];

// ─── PREFERENCE MODAL (2-TAB SEGMENTED CLEAN 4-COL FORMAT) ───────────────────
function PreferenceModal({ initialPref, userProfile, options, quota, onClose, onSave, onGenerate, generating, aiGenerationEnabled = true }) {
  const [activeTab, setActiveTab] = useState("nutrition"); // "nutrition" | "health"

  // Dietary Category
  const [dietType, setDietType] = useState(initialPref?.dietType || "Veg");
  const [customDietType, setCustomDietType] = useState(() => {
    if (initialPref?.customDietType) return initialPref.customDietType;
    if (initialPref?.dietType && !STANDARD_DIETS.includes(initialPref.dietType)) return initialPref.dietType;
    return "";
  });
  const [showCustomDiet, setShowCustomDiet] = useState(false);

  // Cuisine / Region
  const [region, setRegion] = useState(initialPref?.region || "Indian");
  const [customRegion, setCustomRegion] = useState(() => {
    if (initialPref?.customRegion) return initialPref.customRegion;
    if (initialPref?.region && !STANDARD_REGIONS.includes(initialPref.region)) return initialPref.region;
    return "";
  });
  const [showCustomRegion, setShowCustomRegion] = useState(false);

  // Active custom values (recognizes custom inputs even when reloaded from DB)
  const activeCustomDiet = customDietType.trim() || (!STANDARD_DIETS.includes(dietType) ? dietType : "");
  const activeCustomRegion = customRegion.trim() || (!STANDARD_REGIONS.includes(region) ? region : "");

  // Calories & Goal
  const [totalCalories, setTotalCalories] = useState(initialPref?.totalCalories || 1800);
  const [calorieGoal, setCalorieGoal] = useState(initialPref?.calorieGoal || "Low Calorie (Weight Loss)");
  const [customGoal, setCustomGoal] = useState(initialPref?.customGoal || "");
  const [showCustomGoal, setShowCustomGoal] = useState(false);

  // Activity Level
  const [activityLevel, setActivityLevel] = useState(initialPref?.activityLevel || "Moderately Active");

  // Recommended Calorie calculation via Mifflin-St Jeor based on userProfile & activity
  const recommendedCalories = useMemo(() => {
    const weight = Number(userProfile?.weight) || 65;
    const height = Number(userProfile?.height) || 165;
    const age = Number(userProfile?.age) || 28;
    const isMale = (userProfile?.gender || "").toLowerCase() === "male";
    const bmr = 10 * weight + 6.25 * height - 5 * age + (isMale ? 5 : -161);
    const actMult = {
      "Sedentary": 1.2,
      "Lightly Active": 1.375,
      "Moderately Active": 1.55,
      "Very Active": 1.725
    }[activityLevel] || 1.4;
    let tdee = bmr * actMult;
    if (calorieGoal.includes("Weight Loss") || calorieGoal.includes("Low Calorie")) tdee -= 400;
    else if (calorieGoal.includes("Weight Gain") || calorieGoal.includes("High Calorie")) tdee += 350;
    return Math.max(1300, Math.min(3500, Math.round(tdee / 50) * 50));
  }, [userProfile, activityLevel, calorieGoal]);

  const profileMetricsSummary = useMemo(() => {
    const parts = [];
    if (userProfile?.age) parts.push(`${userProfile.age}y`);
    if (userProfile?.gender) parts.push(userProfile.gender.charAt(0).toUpperCase() + userProfile.gender.slice(1).toLowerCase());
    if (userProfile?.height) parts.push(`${userProfile.height}cm`);
    if (userProfile?.weight) parts.push(`${userProfile.weight}kg`);
    return parts.length > 0 ? parts.join(" · ") : null;
  }, [userProfile]);

  // Health Conditions
  const [healthConditions, setHealthConditions] = useState(() => {
    return parseConditionList(initialPref?.healthConditions || userProfile?.healthConditions);
  });
  const [customConditionInput, setCustomConditionInput] = useState("");
  const [showCustomCondition, setShowCustomCondition] = useState(false);

  // Allergies & Exclusions
  const [allergies, setAllergies] = useState(() => {
    return parseConditionList(initialPref?.allergy);
  });
  const [excludedIngredients, setExcludedIngredients] = useState(initialPref?.excludedIngredients || "");

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialPref) {
      if (initialPref.dietType) {
        setDietType(initialPref.dietType);
        if (!STANDARD_DIETS.includes(initialPref.dietType)) {
          setCustomDietType(initialPref.dietType);
        }
      }
      if (initialPref.region) {
        setRegion(initialPref.region);
        if (!STANDARD_REGIONS.includes(initialPref.region)) {
          setCustomRegion(initialPref.region);
        }
      }
      if (initialPref.calorieGoal) setCalorieGoal(initialPref.calorieGoal);
      if (initialPref.totalCalories) setTotalCalories(initialPref.totalCalories);
      if (initialPref.activityLevel) setActivityLevel(initialPref.activityLevel);
      if (initialPref.excludedIngredients) setExcludedIngredients(initialPref.excludedIngredients);
      if (initialPref.healthConditions) {
        setHealthConditions(parseConditionList(initialPref.healthConditions));
      }
      if (initialPref.allergy) {
        setAllergies(parseConditionList(initialPref.allergy));
      }
    } else if (userProfile?.healthConditions) {
      setHealthConditions(parseConditionList(userProfile.healthConditions));
    }
  }, [initialPref, userProfile]);

  // Central Payload Constructor
  const getPayload = (overrides = {}) => {
    const finalDiet = overrides.dietType !== undefined ? overrides.dietType : (activeCustomDiet || dietType);
    const finalRegion = overrides.region !== undefined ? overrides.region : (activeCustomRegion || region);
    const finalGoal = overrides.calorieGoal !== undefined ? overrides.calorieGoal : calorieGoal;
    const finalAllergies = overrides.allergies !== undefined ? overrides.allergies : allergies;
    const finalHealth = overrides.healthConditions !== undefined ? overrides.healthConditions : healthConditions;
    const finalActivity = overrides.activityLevel !== undefined ? overrides.activityLevel : activityLevel;
    const finalCals = overrides.totalCalories !== undefined ? overrides.totalCalories : totalCalories;
    const finalExcluded = overrides.excludedIngredients !== undefined ? overrides.excludedIngredients : excludedIngredients;

    return {
      dietType: finalDiet || "Veg",
      region: finalRegion || "Indian",
      calorieGoal: finalGoal || "Balanced",
      allergy: finalAllergies.length > 0 ? finalAllergies.join(", ") : "None",
      activityLevel: finalActivity || "Moderately Active",
      totalCalories: Number(finalCals) || 1800,
      healthConditions: finalHealth.length > 0 ? finalHealth.join(", ") : "None",
      excludedIngredients: finalExcluded || "",
      customDietType: !STANDARD_DIETS.includes(finalDiet) ? finalDiet : "",
      customRegion: !STANDARD_REGIONS.includes(finalRegion) ? finalRegion : "",
      customGoal: ""
    };
  };

  // Immediate Auto-Save helper: persists to DB & notifies parent component
  const autoSave = async (overrides = {}) => {
    try {
      const payload = getPayload(overrides);
      await dietPlanAPI.saveUserPreference(payload);
      if (onSave) onSave(payload);
    } catch (err) {
      console.warn("Auto-save preference note:", err);
    }
  };

  const handleClose = async () => {
    await autoSave();
    onClose();
  };

  const toggleHealthCondition = (cond) => {
    let next;
    if (cond === "None") {
      next = [];
    } else {
      next = healthConditions.includes(cond) ? healthConditions.filter(c => c !== cond) : [...healthConditions, cond];
    }
    setHealthConditions(next);
    autoSave({ healthConditions: next });
  };

  const addCustomCondition = (e) => {
    e?.preventDefault();
    const trimmed = customConditionInput.trim();
    if (trimmed && !healthConditions.includes(trimmed)) {
      const next = [...healthConditions, trimmed];
      setHealthConditions(next);
      setCustomConditionInput("");
      setShowCustomCondition(false);
      autoSave({ healthConditions: next });
    }
  };

  const toggleAllergy = (alg) => {
    let next;
    if (alg === "None") {
      next = [];
    } else {
      next = allergies.includes(alg) ? allergies.filter(a => a !== alg) : [...allergies, alg];
    }
    setAllergies(next);
    autoSave({ allergies: next });
  };

  const handleSaveOnly = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const payload = getPayload();
      await dietPlanAPI.saveUserPreference(payload);
      if (onSave) onSave(payload);
      onClose();
    } catch (err) {
      console.error("Failed to save preference", err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateClick = async (e) => {
    e?.preventDefault();
    const payload = getPayload();
    await autoSave(payload);
    if (onGenerate) {
      onGenerate(payload);
    }
  };

  const effectiveDietLabel = showCustomDiet && customDietType ? customDietType : (activeCustomDiet || dietType);
  const effectiveRegionLabel = showCustomRegion && customRegion ? customRegion : (activeCustomRegion || region);
  const healthBadgeCount = healthConditions.length + allergies.length + (excludedIngredients.trim() ? 1 : 0);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[300] flex items-center justify-center p-3 sm:p-5 bg-black/50 backdrop-blur-md"
      onClick={handleClose}>
      <motion.div initial={{ scale: 0.95, y: 10, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.95, y: 10, opacity: 0 }}
        transition={{ type: "spring", stiffness: 340, damping: 30 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-2xl bg-white rounded-[32px] p-6 sm:p-8 shadow-2xl border border-zinc-100 overflow-y-auto max-h-[90vh] no-scrollbar flex flex-col justify-between"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
        <style>{`.no-scrollbar::-webkit-scrollbar { display: none; }`}</style>
        
        <div className="space-y-5">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-2xl sm:text-[28px] font-black text-zinc-900 font-['Poppins'] tracking-tight">Diet & Nutrition Preferences</h2>
              <p className="text-xs text-zinc-400 mt-1 font-medium">
                {aiGenerationEnabled ? "Configure your dietary baseline and generate your tailored 7-day meal plan." : "Configure your dietary preferences to find published meal plans."}
              </p>
            </div>
            <div className="flex items-center gap-2.5 shrink-0">
              {aiGenerationEnabled && quota && (
                <span className="font-bold text-[#b45309] bg-amber-50/90 px-3 py-1.5 rounded-full border border-amber-200/80 text-xs shadow-xs flex items-center gap-1.5">
                  <FiZap size={12} className="text-[#ea580c]" />
                  <span>{quota.remaining} / {quota.limit} AI Generations</span>
                </span>
              )}
              <button onClick={handleClose} className="w-8 h-8 rounded-full bg-zinc-100/80 hover:bg-zinc-200 flex items-center justify-center text-zinc-400 hover:text-zinc-700 cursor-pointer border-none transition-colors">
                <FiX size={16} />
              </button>
            </div>
          </div>

          {/* Segmented Tab Switcher: Only shown when AI Personalised Plan is enabled */}
          {aiGenerationEnabled && (
            <div className="p-1 bg-zinc-100/90 rounded-2xl flex gap-1.5 border border-zinc-200/60">
              <button
                type="button"
                onClick={() => setActiveTab("nutrition")}
                className={`flex-1 py-2.5 px-4 rounded-xl text-xs sm:text-[13px] font-bold flex items-center justify-center gap-2 transition-all cursor-pointer border-none ${
                  activeTab === "nutrition"
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "bg-transparent text-zinc-500 hover:text-zinc-800"
                }`}>
                <FiSliders size={13} className={activeTab === "nutrition" ? "text-emerald-700" : "text-zinc-400"} />
                <span>General Preferences</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("health")}
                className={`flex-1 py-2.5 px-4 rounded-xl text-xs sm:text-[13px] font-bold flex items-center justify-center gap-2 transition-all cursor-pointer border-none ${
                  activeTab === "health"
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "bg-transparent text-zinc-500 hover:text-zinc-800"
                }`}>
                <FiZap size={13} className={activeTab === "health" ? "text-orange-500" : "text-zinc-400"} />
                <span>Personalised Plan</span>
                {healthBadgeCount > 0 && (
                  <span className="w-5 h-5 rounded-full bg-[#f97316] text-white text-[10.5px] font-bold flex items-center justify-center shadow-xs ml-0.5">
                    {healthBadgeCount}
                  </span>
                )}
              </button>
            </div>
          )}

          {/* TAB 1: NUTRITION & FLAVORS */}
          {activeTab === "nutrition" && (
            <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }} className="space-y-5">
              {/* Target Summary Strip */}
              <div className="p-3.5 px-4 rounded-2xl bg-[#fef9ee] border border-[#fde6b3]/80 flex items-center justify-between flex-wrap gap-2 text-xs">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <span className="font-extrabold text-[#d97706] flex items-center gap-1.5 uppercase text-[11px] tracking-wider">
                    <FiSliders size={13} className="text-[#d97706]"/> TARGET:
                  </span>
                  <span className="font-bold text-zinc-800 bg-white px-3.5 py-1 rounded-full shadow-xs border border-[#fde6b3] text-xs">
                    {effectiveDietLabel} · {effectiveRegionLabel}
                  </span>
                </div>
              </div>

              {/* Section 1: Dietary Category */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-extrabold text-zinc-500 uppercase tracking-wider">DIETARY CATEGORY</label>
                  {showCustomDiet && (
                    <button type="button" onClick={() => { setShowCustomDiet(false); setCustomDietType(""); }} className="text-[10.5px] font-bold text-emerald-600 hover:underline border-none bg-transparent cursor-pointer">
                      Presets
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {STANDARD_DIETS.map(t => {
                    const isSelected = !activeCustomDiet && dietType === t;
                    return (
                      <button
                        type="button"
                        key={t}
                        onClick={() => {
                          setDietType(t);
                          setCustomDietType("");
                          setShowCustomDiet(false);
                          autoSave({ dietType: t, customDietType: "" });
                        }}
                        className={`py-3 px-2 rounded-2xl text-[12.5px] font-bold transition-all cursor-pointer border flex flex-col items-center justify-center min-h-[46px] ${
                          isSelected
                            ? 'bg-[#00897b] text-white border-[#00897b] shadow-md'
                            : 'bg-white text-zinc-700 border-zinc-200/90 hover:bg-zinc-50 hover:border-zinc-300 shadow-xs'
                        }`}>
                        <span>{t}</span>
                        {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white block mt-0.5" />}
                      </button>
                    );
                  })}
                  {/* Render saved custom diet as an active selectable option button */}
                  {aiGenerationEnabled && activeCustomDiet && !showCustomDiet && (
                    <button
                      type="button"
                      onClick={() => {
                        setDietType(activeCustomDiet);
                        setShowCustomDiet(false);
                        autoSave({ dietType: activeCustomDiet, customDietType: activeCustomDiet });
                      }}
                      className="py-3 px-2 rounded-2xl text-[12.5px] font-bold transition-all cursor-pointer border flex flex-col items-center justify-center min-h-[46px] bg-[#00897b] text-white border-[#00897b] shadow-md relative"
                      title="Custom diet category selected">
                      <span>{activeCustomDiet}</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-white block mt-0.5" />
                    </button>
                  )}
                </div>
                {/* Custom Diet Option */}
                {aiGenerationEnabled && (
                  <div className="mt-2.5">
                    {!showCustomDiet && !activeCustomDiet ? (
                      <button
                        type="button"
                        onClick={() => setShowCustomDiet(true)}
                        className="px-4 py-2 rounded-2xl text-xs font-bold transition-all cursor-pointer border border-emerald-200 bg-emerald-50/60 text-emerald-800 hover:bg-emerald-100/60">
                        + Custom Diet...
                      </button>
                    ) : activeCustomDiet && !showCustomDiet ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => { setCustomDietType(activeCustomDiet); setShowCustomDiet(true); }}
                          className="text-[11px] font-bold text-emerald-700 hover:underline border-none bg-transparent cursor-pointer">
                          Edit "{activeCustomDiet}"
                        </button>
                        <span className="text-zinc-300">·</span>
                        <button
                          type="button"
                          onClick={() => {
                            setCustomDietType("");
                            setDietType("Veg");
                            autoSave({ dietType: "Veg", customDietType: "" });
                          }}
                          className="text-[11px] font-bold text-zinc-400 hover:text-red-500 border-none bg-transparent cursor-pointer">
                          Remove
                        </button>
                      </div>
                    ) : (
                      <motion.div initial={{ opacity: 0, y: -3 }} animate={{ opacity: 1, y: 0 }} className="flex gap-2">
                        <input
                          type="text"
                          autoFocus
                          autoComplete="off"
                          value={customDietType}
                          onChange={(e) => setCustomDietType(e.target.value)}
                          placeholder="e.g., Low-FODMAP, Raw-Vegan, Flexitarian"
                          className="flex-1 px-3.5 py-2 text-xs rounded-xl border border-emerald-300 bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              const val = customDietType.trim();
                              if (val) {
                                setDietType(val);
                                setCustomDietType(val);
                                setShowCustomDiet(false);
                                autoSave({ dietType: val, customDietType: val });
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const val = customDietType.trim();
                            if (val) {
                              setDietType(val);
                              setCustomDietType(val);
                              setShowCustomDiet(false);
                              autoSave({ dietType: val, customDietType: val });
                            }
                          }}
                          disabled={!customDietType.trim()}
                          className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-[#00897b] hover:bg-[#00796b] border-none cursor-pointer transition-colors flex items-center gap-1.5 disabled:opacity-50 shadow-xs">
                          <FiCheck size={13} />
                          <span>Save Option</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowCustomDiet(false)}
                          className="px-3 py-2 rounded-xl text-xs font-semibold text-zinc-500 hover:bg-zinc-100 border border-zinc-200 bg-white cursor-pointer transition-colors">
                          Cancel
                        </button>
                      </motion.div>
                    )}
                  </div>
                )}
              </div>

              {/* Section 2: Cuisine / Region Dimension */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-extrabold text-zinc-500 uppercase tracking-wider">CUISINE / REGION DIMENSION</label>
                  {showCustomRegion && (
                    <button type="button" onClick={() => { setShowCustomRegion(false); setCustomRegion(""); }} className="text-[10.5px] font-bold text-orange-600 hover:underline border-none bg-transparent cursor-pointer">
                      Presets
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {STANDARD_REGIONS.map(r => {
                    const isSelected = !activeCustomRegion && region === r;
                    return (
                      <button
                        type="button"
                        key={r}
                        onClick={() => {
                          setRegion(r);
                          setCustomRegion("");
                          setShowCustomRegion(false);
                          autoSave({ region: r, customRegion: "" });
                        }}
                        className={`py-3 px-2 rounded-2xl text-[12.5px] font-bold transition-all cursor-pointer border flex items-center justify-center gap-1.5 min-h-[46px] ${
                          isSelected
                            ? 'bg-[#f97316] text-white border-[#f97316] shadow-md'
                            : 'bg-white text-zinc-700 border-zinc-200/90 hover:bg-zinc-50 hover:border-zinc-300 shadow-xs'
                        }`}>
                        <span>{r}</span>
                        {isSelected && <FiCheck size={14} className="text-white" />}
                      </button>
                    );
                  })}
                  {/* Render saved custom cuisine as an active option button */}
                  {aiGenerationEnabled && activeCustomRegion && !showCustomRegion && (
                    <button
                      type="button"
                      onClick={() => {
                        setRegion(activeCustomRegion);
                        setShowCustomRegion(false);
                        autoSave({ region: activeCustomRegion, customRegion: activeCustomRegion });
                      }}
                      className="py-3 px-2 rounded-2xl text-[12.5px] font-bold transition-all cursor-pointer border flex items-center justify-center gap-1.5 min-h-[46px] bg-[#f97316] text-white border-[#f97316] shadow-md"
                      title="Custom cuisine selected">
                      <span>{activeCustomRegion}</span>
                      <FiCheck size={14} className="text-white" />
                    </button>
                  )}
                </div>
                {/* Custom Region Option */}
                {aiGenerationEnabled && (
                  <div className="mt-2.5">
                    {!showCustomRegion && !activeCustomRegion ? (
                      <button
                        type="button"
                        onClick={() => setShowCustomRegion(true)}
                        className="px-4 py-2 rounded-2xl text-xs font-bold transition-all cursor-pointer border border-orange-200 bg-orange-50/60 text-orange-800 hover:bg-orange-100/60">
                        + Custom Cuisine...
                      </button>
                    ) : activeCustomRegion && !showCustomRegion ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => { setCustomRegion(activeCustomRegion); setShowCustomRegion(true); }}
                          className="text-[11px] font-bold text-orange-700 hover:underline border-none bg-transparent cursor-pointer">
                          Edit "{activeCustomRegion}"
                        </button>
                        <span className="text-zinc-300">·</span>
                        <button
                          type="button"
                          onClick={() => {
                            setCustomRegion("");
                            setRegion("Indian");
                            autoSave({ region: "Indian", customRegion: "" });
                          }}
                          className="text-[11px] font-bold text-zinc-400 hover:text-red-500 border-none bg-transparent cursor-pointer">
                          Remove
                        </button>
                      </div>
                    ) : (
                      <motion.div initial={{ opacity: 0, y: -3 }} animate={{ opacity: 1, y: 0 }} className="flex gap-2">
                        <input
                          type="text"
                          autoFocus
                          autoComplete="off"
                          value={customRegion}
                          onChange={(e) => setCustomRegion(e.target.value)}
                          placeholder="e.g., Gujarati, Maharashtrian, Korean, Mexican"
                          className="flex-1 px-3.5 py-2 text-xs rounded-xl border border-orange-300 bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-orange-500"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              const val = customRegion.trim();
                              if (val) {
                                setRegion(val);
                                setCustomRegion(val);
                                setShowCustomRegion(false);
                                autoSave({ region: val, customRegion: val });
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const val = customRegion.trim();
                            if (val) {
                              setRegion(val);
                              setCustomRegion(val);
                              setShowCustomRegion(false);
                              autoSave({ region: val, customRegion: val });
                            }
                          }}
                          disabled={!customRegion.trim()}
                          className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-[#f97316] hover:bg-[#ea580c] border-none cursor-pointer transition-colors flex items-center gap-1.5 disabled:opacity-50 shadow-xs">
                          <FiCheck size={13} />
                          <span>Save Option</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowCustomRegion(false)}
                          className="px-3 py-2 rounded-xl text-xs font-semibold text-zinc-500 hover:bg-zinc-100 border border-zinc-200 bg-white cursor-pointer transition-colors">
                          Cancel
                        </button>
                      </motion.div>
                    )}
                  </div>
                )}
              </div>

              {/* Section 3: Fitness & Caloric Goal */}
              <div>
                <label className="text-[11px] font-extrabold text-zinc-500 uppercase tracking-wider block mb-2">FITNESS & CALORIC GOAL</label>
                <CustomSelect label="" value={calorieGoal} onChange={(val) => {
                  setCalorieGoal(val);
                  autoSave({ calorieGoal: val });
                }}
                  options={options?.calorieGoals || [
                    "Balanced Wellness",
                    "Low Calorie (Weight Loss)",
                    "High Calorie (Weight Gain)",
                    "High Protein (Muscle)",
                    "Low Carb / Keto",
                    "Detox / Immunity"
                  ]} />
              </div>

              {/* Section 4: Daily Activity Level (Generic) */}
              <div>
                <label className="text-[11px] font-extrabold text-zinc-500 uppercase tracking-wider block mb-2">
                  DAILY ACTIVITY LEVEL
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {STANDARD_ACTIVITY.map((act) => {
                    const isSelected = activityLevel === act;
                    return (
                      <button
                        type="button"
                        key={act}
                        onClick={() => {
                          setActivityLevel(act);
                          autoSave({ activityLevel: act });
                        }}
                        className={`py-3 px-2 text-center rounded-2xl text-[12.5px] font-bold border transition-all cursor-pointer flex flex-col items-center justify-center min-h-[46px] ${
                          isSelected
                            ? 'bg-[#00897b] text-white border-[#00897b] shadow-md'
                            : 'bg-white text-zinc-700 border-zinc-200/90 hover:bg-zinc-50 shadow-xs'
                        }`}>
                        <span>{act}</span>
                        {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white block mt-0.5" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {/* TAB 2: PERSONALISED AI PLAN */}
          {aiGenerationEnabled && activeTab === "health" && (
            <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }} className="space-y-5">
              {/* Section 5: Daily Calorie Target (Personalised with BMR recommendation) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-extrabold text-zinc-500 uppercase tracking-wider">DAILY CALORIE TARGET</label>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-zinc-400 font-medium">Exact:</span>
                    <input
                      type="number"
                      min="1300"
                      max="4500"
                      step="50"
                      value={totalCalories}
                      onChange={(e) => {
                        const val = Math.max(1300, Number(e.target.value) || 1300);
                        setTotalCalories(val);
                        autoSave({ totalCalories: val });
                      }}
                      className="w-16 px-2 py-1 text-xs font-bold text-center rounded-lg border border-zinc-300 bg-white text-zinc-900 focus:outline-none focus:ring-1 focus:ring-orange-500"
                    />
                    <span className="text-xs text-zinc-400">kcal</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {CALORIE_PRESETS.map(c => {
                    const isSelected = totalCalories === c;
                    return (
                      <button
                        type="button"
                        key={c}
                        onClick={() => {
                          setTotalCalories(c);
                          autoSave({ totalCalories: c });
                        }}
                        className={`py-2.5 px-2 rounded-2xl text-xs font-bold border transition-all cursor-pointer flex flex-col items-center justify-center ${
                          isSelected
                            ? 'bg-[#f97316] text-white border-[#f97316] shadow-md'
                            : 'bg-white text-zinc-700 border-zinc-200/90 hover:bg-zinc-50 shadow-xs'
                        }`}>
                        <span>{c} kcal</span>
                        {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white block mt-0.5" />}
                      </button>
                    );
                  })}
                </div>

                {/* Dedicated Personalized Recommendation Bar */}
                {profileMetricsSummary && (
                  <div className="mt-2.5 flex items-center justify-between px-3.5 py-2 rounded-xl bg-orange-50/60 border border-orange-200/70 text-xs">
                    <div className="flex items-center gap-2 text-zinc-700">
                      <FiZap size={13} className="text-[#ea580c] flex-shrink-0" />
                      <span>Recommended: <strong className="text-zinc-900 font-extrabold">{recommendedCalories} kcal</strong></span>
                      <span className="text-zinc-400 text-[11px] hidden sm:inline">(Profile: {profileMetricsSummary})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setTotalCalories(recommendedCalories);
                        autoSave({ totalCalories: recommendedCalories });
                      }}
                      className={`px-3 py-1 rounded-lg font-bold text-[11px] border-none cursor-pointer transition-all ${
                        totalCalories === recommendedCalories
                          ? 'bg-orange-100 text-orange-900 font-extrabold cursor-default'
                          : 'bg-[#f97316] text-white hover:bg-[#ea580c] shadow-xs'
                      }`}>
                      {totalCalories === recommendedCalories ? 'Active' : 'Apply'}
                    </button>
                  </div>
                )}
              </div>
              {/* Section 5: Health Focus & Safeguards */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-extrabold text-zinc-500 uppercase tracking-wider">
                    HEALTH FOCUS & SAFEGUARDS
                  </label>
                  {healthConditions.length > 0 && (
                    <button type="button" onClick={() => toggleHealthCondition("None")} className="text-[10.5px] font-bold text-zinc-400 hover:text-zinc-600 border-none bg-transparent cursor-pointer">
                      Clear All
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {STANDARD_HEALTH_CONDITIONS.map(cond => {
                    const isNone = cond === "None";
                    const isSelected = isNone ? healthConditions.length === 0 : healthConditions.includes(cond);
                    return (
                      <button
                        type="button"
                        key={cond}
                        onClick={() => toggleHealthCondition(cond)}
                        className={`py-3 px-2 rounded-2xl text-[12.5px] font-bold transition-all cursor-pointer border flex flex-col items-center justify-center min-h-[46px] ${
                          isSelected
                            ? 'bg-[#00897b] text-white border-[#00897b] shadow-md'
                            : 'bg-white text-zinc-700 border-zinc-200/90 hover:bg-zinc-50 hover:border-zinc-300 shadow-xs'
                        }`}>
                        <span>{cond}</span>
                        {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white block mt-0.5" />}
                      </button>
                    );
                  })}
                </div>
                {/* Custom Health Conditions & Add Button */}
                <div className="mt-2.5 flex flex-wrap gap-2 items-center">
                  {healthConditions.filter(c => !STANDARD_HEALTH_CONDITIONS.includes(c) && c !== "None").map(customCond => (
                    <button
                      type="button"
                      key={customCond}
                      onClick={() => toggleHealthCondition(customCond)}
                      className="py-1.5 px-3 rounded-xl text-xs font-bold border transition-all cursor-pointer bg-[#00897b] text-white border-[#00897b] shadow-xs flex items-center gap-1.5">
                      <span>{customCond}</span>
                      <FiX size={12} className="text-white/80" />
                    </button>
                  ))}
                  {!showCustomCondition ? (
                    <button
                      type="button"
                      onClick={() => setShowCustomCondition(true)}
                      className="px-4 py-2 rounded-2xl text-xs font-bold transition-all cursor-pointer border border-emerald-200 bg-emerald-50/60 text-emerald-800 hover:bg-emerald-100/60">
                      + Add Condition...
                    </button>
                  ) : (
                    <motion.div initial={{ opacity: 0, y: -3 }} animate={{ opacity: 1, y: 0 }} className="flex gap-2 flex-1 min-w-[240px]">
                      <input
                        type="text"
                        value={customConditionInput}
                        onChange={(e) => setCustomConditionInput(e.target.value)}
                        placeholder="e.g., Fatty Liver, Uric Acid, Migraines"
                        className="flex-1 px-3.5 py-2 text-xs rounded-xl border border-emerald-300 bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        onKeyDown={(e) => { if (e.key === "Enter") addCustomCondition(e); }}
                      />
                      <button
                        type="button"
                        onClick={addCustomCondition}
                        className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-700 hover:bg-emerald-800 border-none cursor-pointer">
                        Add
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowCustomCondition(false); setCustomConditionInput(""); }}
                        className="px-3 py-2 rounded-xl text-xs font-semibold text-zinc-500 hover:bg-zinc-100 border border-zinc-200 bg-white cursor-pointer">
                        Cancel
                      </button>
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Section 6: Allergens & Food Exclusions */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-extrabold text-zinc-500 uppercase tracking-wider">
                    ALLERGENS & FOOD EXCLUSIONS
                  </label>
                  {allergies.length > 0 && (
                    <button type="button" onClick={() => toggleAllergy("None")} className="text-[10.5px] font-bold text-zinc-400 hover:text-zinc-600 border-none bg-transparent cursor-pointer">
                      Clear All
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {STANDARD_ALLERGENS.map(alg => {
                    const isNone = alg === "None";
                    const isSelected = isNone ? allergies.length === 0 : allergies.includes(alg);
                    return (
                      <button
                        type="button"
                        key={alg}
                        onClick={() => toggleAllergy(alg)}
                        className={`py-3 px-2 rounded-2xl text-[12.5px] font-bold transition-all cursor-pointer border flex flex-col items-center justify-center min-h-[46px] ${
                          isSelected
                            ? 'bg-[#00897b] text-white border-[#00897b] shadow-md'
                            : 'bg-white text-zinc-700 border-zinc-200/90 hover:bg-zinc-50 hover:border-zinc-300 shadow-xs'
                        }`}>
                        <span>{alg}</span>
                        {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white block mt-0.5" />}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2.5">
                  <input
                    type="text"
                    value={excludedIngredients}
                    onChange={(e) => setExcludedIngredients(e.target.value)}
                    onBlur={() => autoSave({ excludedIngredients })}
                    placeholder="Disliked / Excluded Ingredients (e.g., No mushrooms, no bitter gourd, no raw onions)"
                    className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-zinc-200 bg-zinc-50/50 text-zinc-800 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Modal Footer: Adaptive per Tab */}
        <div className="flex items-center justify-between flex-wrap gap-3 pt-5 mt-6 border-t border-zinc-100">
          {!aiGenerationEnabled ? (
            <>
              <button type="button" onClick={handleClose}
                className="h-11 px-6 rounded-full text-zinc-600 font-bold text-xs bg-zinc-100 hover:bg-zinc-200 border-none cursor-pointer transition-colors">
                Close
              </button>
              <button type="button" disabled={loading} onClick={handleSaveOnly}
                className="h-11 px-7 rounded-full text-white font-extrabold text-xs bg-[#00897b] hover:bg-[#00796b] border-none cursor-pointer transition-all shadow-md">
                {loading ? "Saving..." : "Save Preferences"}
              </button>
            </>
          ) : activeTab === "nutrition" ? (
            <>
              <button type="button" disabled={loading || generating} onClick={handleSaveOnly}
                className="h-11 px-6 rounded-full text-zinc-600 font-bold text-xs bg-zinc-100 hover:bg-zinc-200 border-none cursor-pointer transition-colors">
                {loading ? "Saving..." : "Save Preferences"}
              </button>
              <button type="button" onClick={() => setActiveTab("health")}
                className="h-11 px-6 rounded-full text-white font-bold text-xs bg-[#f97316] hover:bg-[#ea580c] border-none cursor-pointer transition-colors flex items-center gap-1.5 shadow-sm">
                <span>Next: Personalised Plan</span>
                <FiArrowRight size={13} />
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setActiveTab("nutrition")}
                  className="h-11 px-5 rounded-full text-zinc-600 font-bold text-xs bg-zinc-100 hover:bg-zinc-200 border-none cursor-pointer transition-colors">
                  ← General
                </button>
                <button type="button" disabled={loading || generating} onClick={handleSaveOnly}
                  className="h-11 px-5 rounded-full text-zinc-500 font-semibold text-xs hover:bg-zinc-100 border border-zinc-200/80 bg-white cursor-pointer transition-colors">
                  {loading ? "Saving..." : "Save Preferences Only"}
                </button>
              </div>
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} disabled={loading || generating} type="button" onClick={handleGenerateClick}
                className="flex-1 sm:flex-none sm:min-w-[280px] h-11 px-7 rounded-full text-white font-black text-[13.5px] border-none cursor-pointer flex items-center justify-center gap-2 shadow-lg transition-all"
                style={{ background: `linear-gradient(135deg, ${T.orange}, ${T.orange2})`, boxShadow: "0 6px 20px rgba(242,154,12,0.35)" }}>
                {generating ? (
                  <div className="flex items-center gap-2.5">
                    <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin flex-shrink-0" />
                    <span>Generating Tailored 7-Day Plan...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <FiZap size={13} className="text-white fill-white"/>
                    <span>Generate Personalised 7-Day Plan</span>
                    <FiArrowRight size={14} className="text-white ml-0.5" />
                  </div>
                )}
              </motion.button>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function DietPlanPage() {
  const { checkedIn, checkIn, loading: checkInLoading } = useCheckIn();

  // Dynamic Today Index (0=Mon, 1=Tue, 2=Wed, 3=Thu, 4=Fri, 5=Sat, 6=Sun)
  const todayIdx = useMemo(() => {
    const dayNum = new Date().getDay();
    return dayNum === 0 ? 6 : dayNum - 1;
  }, []);

  const [activeDay, setActiveDay] = useState(() => {
    const dayNum = new Date().getDay();
    return dayNum === 0 ? 6 : dayNum - 1;
  });

  const [checkedMeals, setCheckedMeals] = useState(() => {
    const init = {};
    DAYS.forEach((_, i) => { init[i] = new Set(); });
    return init;
  });
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    userAPI.getProfile().then(res => {
      if (res.data?.data) setUserProfile(res.data.data);
    }).catch(() => null);
  }, []);

  const [upgradeModal, setUpgradeModal] = useState(null);
  const [quotaModal, setQuotaModal] = useState(null);
  const [dayCompleteModal, setDayCompleteModal] = useState(null);
  const [errorModal, setErrorModal] = useState(null);

  // Initial water intake = 0 glasses as requested
  const [waterGlasses, setWaterGlasses] = useState(0);
  const [userPref, setUserPref] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [userTier, setUserTier] = useState("Free");
  const [showPrefModal, setShowPrefModal] = useState(false);
  const [dbPlan, setDbPlan] = useState(null);
  const [quota, setQuota] = useState(null);
  const [generatingAi, setGeneratingAi] = useState(false);
  const [aiGenerationEnabled, setAiGenerationEnabled] = useState(false);
  const [customisePrefsEnabled, setCustomisePrefsEnabled] = useState(false);
  const [dynamicOptions, setDynamicOptions] = useState({
    dietTypes: ["Veg","Non-Veg","Vegan","Eggetarian","Jain","Pescatarian","Keto","Sattvic"],
    regions: ["Indian","Asian","Indian (North)","Indian (South)","Western","Mediterranean","Middle Eastern","Global"],
    calorieGoals: ["Balanced","Low Calorie (Weight Loss)","Balanced Wellness","High Calorie (Weight Gain)","High Protein (Muscle)","Low Carb / Keto","Detox / Immunity"]
  });

  const fetchQuota = async () => {
    try {
      const res = await dietPlanAPI.getDietQuota();
      if (res.data?.data) {
        setQuota(res.data.data);
      }
    } catch (e) {
      console.warn("Diet quota load note:", e);
    }
  };

  const fetchDietData = async (overridePref = null) => {
    try {
      let currentPref = overridePref;
      if (overridePref) {
        setUserPref(overridePref);
        setWaterGlasses(overridePref.waterIntakeGss === 4 ? 0 : (overridePref.waterIntakeGss ?? 0));
      } else {
        const prefRes = await dietPlanAPI.getUserPreference();
        if (prefRes.data?.data) {
          currentPref = prefRes.data.data;
          setUserPref(currentPref);
          setWaterGlasses(currentPref.waterIntakeGss === 4 ? 0 : (currentPref.waterIntakeGss ?? 0));
        }
      }

      // Fetch all admin plans to dynamically extract category dimensions
      try {
        const allPlansRes = await dietPlanAPI.getAdminPlans();
        if (allPlansRes.data?.data) {
          const published = allPlansRes.data.data.filter(p => p.status === "Published");
          
          const dietTypesSet = new Set(["Veg","Non-Veg","Vegan","Eggetarian","Jain","Pescatarian","Keto","Sattvic"]);
          const regionsSet = new Set(["Indian","Asian","Indian (North)","Indian (South)","Western","Mediterranean","Middle Eastern","Global"]);
          const calorieGoalsSet = new Set(["Balanced","Low Calorie (Weight Loss)","Balanced Wellness","High Calorie (Weight Gain)","High Protein (Muscle)","Low Carb / Keto","Detox / Immunity"]);

          published.forEach(p => {
            if (p.dietType) dietTypesSet.add(p.dietType);
            if (p.region) regionsSet.add(p.region);
            if (p.calorieGoal) calorieGoalsSet.add(p.calorieGoal);
          });

          setDynamicOptions({
            dietTypes: Array.from(dietTypesSet),
            regions: Array.from(regionsSet),
            calorieGoals: Array.from(calorieGoalsSet)
          });
        }
      } catch (err) {
        console.warn("Failed to fetch dynamic dimensions, using defaults:", err);
      }

      const planRes = await dietPlanAPI.getMyPlan(
        currentPref?.dietType || "",
        currentPref?.region || "",
        currentPref?.calorieGoal || ""
      );
      const planData = planRes.data?.data || planRes.data;

      if (planData?.userTier) {
        setUserTier(planData.userTier);
      }
      if (planData?.plan) {
        setDbPlan(planData.plan);
      } else if (planData && (planData.meals || planData.Meals)) {
        setDbPlan(planData);
      } else {
        setDbPlan(null);
      }

      // Map saved database meal completion checkmarks across all days
      const completedMealsList = planData?.completedMeals || planRes.data?.completedMeals;
      const allPlanMeals = planData?.plan?.meals || planData?.meals || [];
      if (completedMealsList && allPlanMeals.length > 0) {
        const initialChecked = {};
        DAYS.forEach((_, i) => { initialChecked[i] = new Set(); });

        const allMeals = allPlanMeals;

        FULL_DAYS.forEach((dayName, dayIdx) => {
          const sortedMealsForDay = allMeals
            .filter(m => (m.day || "").toLowerCase().trim() === dayName.toLowerCase().trim())
            .sort((a, b) => getMealChronologicalScore(a) - getMealChronologicalScore(b));

          const dayLogs = completedMealsList.filter(c => 
            (c.day || c.Day || "").toLowerCase().trim() === dayName.toLowerCase().trim()
          );

          dayLogs.forEach(c => {
            const loggedMealName = (c.mealName || c.MealName || "").toLowerCase().trim();
            const mealIdx = sortedMealsForDay.findIndex(m => 
              (m.mealName || "").toLowerCase().trim() === loggedMealName
            );
            if (mealIdx !== -1) {
              initialChecked[dayIdx].add(mealIdx);
            }
          });
        });

        setCheckedMeals(initialChecked);
      } else {
        const initialChecked = {};
        DAYS.forEach((_, i) => { initialChecked[i] = new Set(); });
        setCheckedMeals(initialChecked);
      }
    } catch (e) {
      console.warn("Diet preference load note:", e);
    }
  };

  const handleGenerateMyDietPlan = async (prefData, redeemPoints = false) => {
    setGeneratingAi(true);
    try {
      const payload = {
        dietType: prefData.dietType || "Veg",
        region: prefData.region || "Indian",
        calorieGoal: prefData.calorieGoal || "Balanced",
        allergy: prefData.allergy || "None",
        activityLevel: prefData.activityLevel || "Moderately Active",
        totalCalories: prefData.totalCalories || 1800,
        redeemPoints: redeemPoints,
        healthConditions: prefData.healthConditions || "",
        excludedIngredients: prefData.excludedIngredients || "",
        customDietType: prefData.customDietType || "",
        customRegion: prefData.customRegion || "",
        customGoal: prefData.customGoal || "",
        mealSchedule: prefData.mealSchedule || ""
      };

      const res = await dietPlanAPI.generateMyPlan(payload);
      if (res.data?.data) {
        setDbPlan(res.data.data);
        setUserPref(payload);
        if (res.data.quota) {
          setQuota(prev => ({ ...prev, ...res.data.quota }));
        }
        setShowPrefModal(false);
        setQuotaModal(null);
        await fetchDietData(payload);
        await fetchQuota();
      }
    } catch (err) {
      const errData = err.response?.data;
      const requireUpgrade = errData?.requireUpgrade ?? errData?.RequireUpgrade;
      const quotaExceeded = errData?.quotaExceeded ?? errData?.QuotaExceeded;

      let msg = errData?.message || errData?.Message || "";
      if (!msg && typeof errData === "string" && errData.trim().length > 0) {
        msg = errData;
      }
      if (!msg && err.response?.status === 429) {
        msg = "You are generating AI plans too quickly. Please wait a minute before trying again.";
      }
      if (!msg) {
        msg = err.response?.statusText || err.message || "Failed to generate AI Diet Plan. Please try again.";
      }

      if (requireUpgrade) {
        setShowPrefModal(false);
        setUpgradeModal({
          title: "AI Nutritionist Required",
          description: msg
        });
      } else if (quotaExceeded) {
        setShowPrefModal(false);
        setQuotaModal({
          message: msg,
          canRedeemPoints: errData?.canRedeemPoints ?? errData?.CanRedeemPoints,
          userPoints: errData?.userPoints ?? errData?.UserPoints ?? quota?.userPoints ?? 0,
          pendingPref: prefData
        });
      } else {
        setErrorModal(msg);
      }
    } finally {
      setGeneratingAi(false);
    }
  };

  useEffect(() => {
    setInitialLoading(true);
    Promise.all([
      fetchDietData(),
      fetchQuota(),
      userAPI.getUserSubscription().then(res => {
        const feats = res.data?.data?.activeSubscription?.allowedFeatures
          || res.data?.activeSubscription?.allowedFeatures;
        if (Array.isArray(feats)) {
          setAiGenerationEnabled(feats.includes("ai_diet_plan"));
          setCustomisePrefsEnabled(feats.includes("customise_preferences"));
        }
      }).catch(() => {})
    ]).finally(() => {
      setInitialLoading(false);
    });
  }, []);

  const handleUpdateWater = async (newVal) => {
    const clamped = Math.max(0, Math.min(12, newVal));
    setWaterGlasses(clamped);
    try {
      await dietPlanAPI.saveUserPreference({
        dietType: userPref?.dietType || "Veg",
        region: userPref?.region || "Indian",
        calorieGoal: userPref?.calorieGoal || "Balanced",
        allergy: userPref?.allergy || "None",
        activityLevel: userPref?.activityLevel || "Moderately Active",
        waterIntakeGss: clamped
      });
      setUserPref(prev => prev ? { ...prev, waterIntakeGss: clamped } : { waterIntakeGss: clamped });
    } catch (e) {
      console.error("Water update error", e);
    }
  };

  // Strictly render meals fetched directly from backend API sorted chronologically
  const dayMealsList = useMemo(() => {
    if (!dbPlan || !Array.isArray(dbPlan.meals)) return [];
    const dayName = FULL_DAYS[activeDay];
    return dbPlan.meals
      .filter(m => m.day === dayName)
      .sort((a, b) => getMealChronologicalScore(a) - getMealChronologicalScore(b));
  }, [dbPlan, activeDay]);

  const targetDailyCal = useMemo(() => {
    if (dbPlan?.totalCalories) return dbPlan.totalCalories;
    return 0;
  }, [dbPlan]);

  const todayTotals = useMemo(() => {
    let cal = 0, protein = 0, carbs = 0, fat = 0;
    dayMealsList.forEach((m, idx) => {
      if (checkedMeals[activeDay]?.has(idx)) {
        cal += m.calories;
        protein += m.protein;
        carbs += m.carbs;
        fat += m.fat;
      }
    });
    return { cal, protein, carbs, fat };
  }, [dayMealsList, checkedMeals, activeDay]);

  const totalMealsCompleted = Object.values(checkedMeals).reduce((s, v) => s + v.size, 0);

  const toggleMeal = async (dayIdx, mealIdx) => {
    const mealItem = dayMealsList[mealIdx];
    let isNowCompleted = false;

    setCheckedMeals(prev => {
      const next = { ...prev, [dayIdx]: new Set(prev[dayIdx]) };
      if (next[dayIdx].has(mealIdx)) {
        next[dayIdx].delete(mealIdx);
        isNowCompleted = false;
      } else {
        next[dayIdx].add(mealIdx);
        isNowCompleted = true;
        if (next[dayIdx].size >= dayMealsList.length && dayMealsList.length > 0) {
          setTimeout(() => setDayCompleteModal(FULL_DAYS[dayIdx]), 400);
        }
      }
      return next;
    });

    if (mealItem) {
      try {
        await dietPlanAPI.toggleMealLog({
          day: FULL_DAYS[dayIdx],
          mealName: mealItem.mealName,
          isCompleted: isNowCompleted
        });
      } catch (err) {
        console.error("Failed to persist meal completion status:", err);
      }
    }
  };

  const macros = [
    { label: "Calories", val: todayTotals.cal, max: targetDailyCal || 0, unit: "kcal", color: `linear-gradient(90deg,${T.orange},${T.orange2})`, track: "rgba(242,154,12,0.12)" },
    { label: "Protein",  val: todayTotals.protein, max: Math.round((targetDailyCal || 0) * 0.25 / 4), unit: "g", color: `linear-gradient(90deg,${T.olive},${T.olive2})`,   track: "rgba(107,138,90,0.12)" },
    { label: "Carbs",    val: todayTotals.carbs, max: Math.round((targetDailyCal || 0) * 0.45 / 4), unit: "g",   color: `linear-gradient(90deg,${T.orange2},#C8600F)`,      track: "rgba(233,120,24,0.1)" },
    { label: "Fat",      val: todayTotals.fat, max: Math.round((targetDailyCal || 0) * 0.30 / 9), unit: "g",      color: "linear-gradient(90deg,#534AB7,#7F77DD)",           track: "rgba(83,74,183,0.1)" },
  ];

  if (initialLoading) {
    return (
      <UserDashboardLayout>
        <Topbar />
        <BrandLoader 
          message="Loading Personalized Diet Plans..." 
          subMessage="Fetching your nutrition recommendations & meal schedule" 
        />
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div style={{ fontFamily: "'Inter',sans-serif" }}>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@700;800;900&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

        {/* Ambient orbs */}
        <motion.div animate={{ y: [0,-24,0], x:[0,14,0] }} transition={{ duration:13, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[460px] h-[460px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(107,138,90,0.09),transparent 70%)", filter:"blur(80px)", top:-150, right:-100 }} />
        <motion.div animate={{ y:[0,18,0], x:[0,-10,0] }} transition={{ duration:16, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[380px] h-[380px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.07),transparent 70%)", filter:"blur(80px)", bottom:-80, left:-80 }} />
        <div className="relative w-full min-w-0 z-10">
          <Topbar/>
          {!checkedIn ? (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center p-12 text-center rounded-[36px] mt-6"
              style={{ ...glass, minHeight: 450 }}
            >
              <div className="w-16 h-16 rounded-[20px] flex items-center justify-center mb-6"
                style={{ background: "rgba(107, 138, 90, 0.12)", border: "1px solid rgba(107, 138, 90, 0.25)" }}>
                <GiLotus className="text-[36px]" style={{ color: T.olive }} />
              </div>
              <h2 className="text-[22px] font-black text-gray-800 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Check-In Required ✦
              </h2>
              <p className="text-sm text-gray-500 max-w-[360px] leading-relaxed mb-8">
                Please check in for the day to unlock your personalized weekly nutrition plan, track calorie goals, and record your meals.
              </p>
              <motion.button
                whileHover={{ y: -3, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={checkInLoading}
                onClick={checkIn}
                className="h-[52px] px-8 rounded-full text-white font-bold text-sm shadow-[0_12px_30px_rgba(107,138,90,0.35)] cursor-pointer flex items-center gap-2"
                style={{ background: "linear-gradient(135deg, #6B8A5A, #8BA47A)", border: "none" }}
              >
                Check In Now
              </motion.button>
            </motion.div>
          ) : (
            <>
              {/* ── HERO ── */}
              <motion.div initial={{ opacity:0, y:24 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.7 }}
            className="relative overflow-hidden rounded-[36px] p-6 sm:p-8 md:p-10 mb-6" style={glass}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.03] rounded-[inherit]"
              style={{ backgroundImage:"radial-gradient(circle,#000 1px,transparent 0)", backgroundSize:"22px 22px" }} />
            <div className="absolute top-0 right-0 pointer-events-none"
              style={{ width:280, height:280, background:"radial-gradient(circle,rgba(107,138,90,0.1),transparent)", filter:"blur(40px)" }} />

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] xl:grid-cols-[1fr_300px] gap-6 lg:gap-8 items-center relative">
              <div>
                <div className="flex items-center gap-3 mb-5 flex-wrap">
                  <div className="w-11 h-11 rounded-[16px] flex items-center justify-center"
                    style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`, boxShadow:`0 8px 22px rgba(107,138,90,0.45)` }}>
                    <FiFramer size={20} style={{ color:"#fff" }} />
                  </div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[1.5px]" style={{ color:T.olive }}>Diet Plan</p>
                    <p className="text-[12px]" style={{ color:T.light }}>{userTier} Tier · Personalised weekly nutrition</p>
                  </div>
                  <div className="ml-auto sm:ml-4 flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-bold flex-shrink-0"
                    style={{ background: dbPlan ? "rgba(107,138,90,0.1)" : "rgba(242,154,12,0.1)", border: dbPlan ? "1px solid rgba(107,138,90,0.2)" : "1px solid rgba(242,154,12,0.2)", color: dbPlan ? "#27500A" : "#D97706" }}>
                    <FiFramer size={11} /> {dbPlan ? `${dbPlan.dietType} · ${dbPlan.region} Plan` : `${userPref?.dietType || 'Veg'} · ${userPref?.region || 'Indian'} (Unpublished)`}
                  </div>
                </div>
                <h1 className="font-black mb-3 text-[26px] sm:text-[32px] lg:text-[38px]" style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-1.5px", lineHeight:1.06, color:T.dark }}>
                  Nourish your body,{" "}
                  <span style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
                    fuel your practice.
                  </span>
                </h1>
                <p className="text-[14px] leading-[1.8] mb-7" style={{ color: T.muted, maxWidth: 580 }}>
                  {dbPlan ? (dbPlan.description || `Your official published nutrition plan for ${dbPlan.name} (${dbPlan.dietType} · ${dbPlan.region}).`) : `No published diet plan is active for your selected category (${userPref?.dietType || 'Veg'} · ${userPref?.region || 'Indian'}) under ${userTier} Tier.`}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  {customisePrefsEnabled && (
                  <motion.button whileHover={{ y:-2, scale: 1.02 }} whileTap={{ scale:0.97 }}
                    onClick={() => setShowPrefModal(true)}
                    className="h-11 px-6 rounded-full text-white text-[13px] font-extrabold flex items-center gap-2 shadow-lg cursor-pointer"
                    style={{ background: `linear-gradient(135deg, ${T.orange}, ${T.orange2})`, border: "none", boxShadow: "0 8px 24px rgba(242,154,12,0.4)" }}>
                    <FiSliders size={15} /> Customise Preferences
                  </motion.button>
                  )}
                  {dbPlan && (
                    <motion.button whileHover={{ y:-2 }} whileTap={{ scale:0.97 }}
                      onClick={() => downloadDietPlanPDF(dbPlan, userPref)}
                      className="h-11 px-6 rounded-full text-white text-[13px] font-bold flex items-center gap-2"
                      style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`, boxShadow:`0 8px 22px rgba(107,138,90,0.4)`, border:"none", cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                      <FiDownload size={14} /> Download PDF
                    </motion.button>
                  )}
                </div>
              </div>

              <TiltCard intensity={5} className="rounded-[24px] p-6" style={{ background:"rgba(255,255,255,0.7)", border:`1px solid ${T.border}` }}>
                <p className="text-[10px] font-bold uppercase tracking-[1px] text-center mb-4" style={{ color:T.light }}>Today's Nutrition</p>
                <MacroRing 
                  consumed={todayTotals.cal} 
                  target={targetDailyCal} 
                  completedMeals={checkedMeals[activeDay]?.size || 0} 
                  totalMeals={dayMealsList.length} 
                />
                <div className="grid grid-cols-3 gap-2 mt-4">
                  {[["Protein", todayTotals.protein + "g", T.orange, "#FFF4E8"], ["Carbs", todayTotals.carbs + "g", T.olive, "#F5FAF2"], ["Fat", todayTotals.fat + "g", T.orange2, "#FFF4E8"]].map(([l,v,c,bg]) => (
                    <div key={l} className="rounded-[12px] p-2.5 text-center" style={{ background:bg }}>
                      <p className="font-black text-[#1F1F1F] text-[15px]" style={{ fontFamily:"'Poppins',sans-serif" }}>{v}</p>
                      <p className="text-[9px] font-bold uppercase tracking-wide mt-0.5" style={{ color:c }}>{l}</p>
                    </div>
                  ))}
                </div>
              </TiltCard>
            </div>
          </motion.div>


          {/* ── STATS BAND ── */}
          <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.1 }}
            className="ud-kpi grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-0 mb-6 rounded-[24px] overflow-hidden"
            style={{ border:"1px solid rgba(255,255,255,0.9)", boxShadow:"0 12px 40px rgba(0,0,0,0.05)" }}>
            {[
              { icon:FiZap,         val:`${targetDailyCal}`,  label:"Daily Target kcal",   accent:T.orange, bg:"linear-gradient(145deg,#FFF4E8,#FFF8EE)" },
              { icon:FiCheck,       val:String(totalMealsCompleted), label:"Meals Completed", accent:T.olive,  bg:"linear-gradient(145deg,#F5FAF2,#F9FDF5)" },
              { icon:FiDroplet,     val:`${(waterGlasses * 0.25).toFixed(1)}L`, label:"Water Today", accent:T.orange, bg:"linear-gradient(145deg,#FFF4E8,#FFF8EE)" },
              { icon:FiTrendingUp,  val: (dayMealsList.length > 0 && targetDailyCal > 0) ? `${Math.round((todayTotals.cal / targetDailyCal) * 100)}%` : "0%", label:"Calorie Target Met", accent:T.olive, bg:"linear-gradient(145deg,#F5FAF2,#F9FDF5)" },
            ].map((s,i) => (
              <motion.div key={s.label} whileHover={{ background:"rgba(255,255,255,0.95)" }}
                className="relative overflow-hidden px-6 py-5 flex items-center gap-3"
                style={{ background:s.bg, borderRight:i<3?`1px solid rgba(239,229,216,0.5)`:"none" }}>
                <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background:`linear-gradient(90deg,${s.accent},${s.accent}88)` }} />
                <div className="w-9 h-9 rounded-[12px] flex items-center justify-center flex-shrink-0"
                  style={{ background:`linear-gradient(135deg,${s.accent},${s.accent}CC)`, boxShadow:`0 6px 16px ${s.accent}40` }}>
                  <s.icon size={16} style={{ color:"#fff" }} />
                </div>
                <div>
                  <p className="ud-kpi-val font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", fontSize:24, letterSpacing:"-1px", lineHeight:1 }}>{s.val}</p>
                  <p className="ud-kpi-lbl text-[10px] font-bold uppercase tracking-[0.7px]" style={{ color:T.light }}>{s.label}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* ── DAY SELECTOR ── */}
          <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.14 }} className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-black text-[22px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.5px" }}>
                  {dbPlan ? `${dbPlan.name} (${dbPlan.dietType} · ${dbPlan.region})` : `${userPref?.dietType || 'Veg'} · ${userPref?.region || 'Indian'} (No Active Plan)`}
                </h2>
                <p className="text-[13px] mt-0.5" style={{ color:T.muted }}>
                  {userTier} Tier · {userPref?.calorieGoal || 'Balanced'} Goal · Click a day to view menu
                </p>
              </div>
              {customisePrefsEnabled && (
                <div className="flex gap-2">
                  <button type="button" onClick={() => setShowPrefModal(true)}
                    className="h-9 px-4 rounded-full text-[12px] font-bold flex items-center gap-1.5 cursor-pointer border"
                    style={{ background:"rgba(107,138,90,0.1)", borderColor:"rgba(107,138,90,0.25)", color:T.olive }}>
                    <FiSliders size={13}/> Customize Preferences
                  </button>
                </div>
              )}
            </div>

            {/* Dynamic Days List with actual Today pill matching */}
            <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth:"none" }}>
              {DAYS.map((d, i) => {
                const isActive = i === activeDay;
                const isToday = i === todayIdx;
                const daySpecificMeals = dbPlan?.meals?.filter(m => m.day === FULL_DAYS[i]) || [];
                const isDone = daySpecificMeals.length > 0 && (checkedMeals[i]?.size || 0) >= daySpecificMeals.length;
                return (
                  <motion.button key={d} onClick={() => setActiveDay(i)}
                    whileTap={{ scale:0.94 }}
                    className="flex-shrink-0 rounded-full text-center border-0 outline-none cursor-pointer relative"
                    style={{
                      padding:"10px 18px", minWidth:82,
                      background: isActive ? `linear-gradient(135deg,${T.orange},${T.orange2})` : "rgba(255,255,255,0.82)",
                      color: isActive ? "#fff" : "#777",
                      boxShadow: isActive ? `0 8px 22px rgba(242,154,12,0.38)` : "none",
                      border: isActive ? "none" : `1px solid ${T.border}`,
                      fontFamily:"'Inter',sans-serif",
                      transform: isActive ? "translateY(-2px)" : "none",
                    }}>
                    <div className="text-[10px] font-semibold opacity-75 mb-0.5">{d}</div>
                    <div className="text-[14px] font-black">{FULL_DAYS[i].slice(0,3)}</div>
                    {isToday && (
                      <div className="text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded-full mt-1"
                        style={{
                          background: isActive ? "#fff" : T.orange,
                          color: isActive ? T.orange : "#fff"
                        }}>
                        Today
                      </div>
                    )}
                    {isDone && !isToday && (
                      <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full"
                        style={{ background: isActive ? "#fff" : T.olive }} />
                    )}
                  </motion.button>
                );
              })}
            </div>
          </motion.div>

          {/* ── MAIN GRID ── */}
          <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.18 }}
            className="grid grid-cols-1 lg:grid-cols-[1fr_290px] xl:grid-cols-[1fr_320px] gap-5">

            {/* Meal cards */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-black text-[18px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.4px" }}>
                    {FULL_DAYS[activeDay]}'s {dbPlan?.dietType || userPref?.dietType || 'Veg'} Meals
                  </h2>
                  <p className="text-[12px] mt-0.5" style={{ color:T.muted }}>
                    {checkedMeals[activeDay]?.size || 0} of {dayMealsList.length} completed · {todayTotals.cal} / {targetDailyCal} kcal
                  </p>
                </div>
                <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-bold"
                  style={{ background:"rgba(107,138,90,0.1)", border:"1px solid rgba(107,138,90,0.2)", color:"#27500A" }}>
                  <FiFramer size={11} />
                  {activeDay === 5 || activeDay === 6 ? "Rest Day" : "Training Day"}
                </div>
              </div>

              <AnimatePresence mode="wait">
                <motion.div key={activeDay} initial={{ opacity:0, x:-16 }} animate={{ opacity:1, x:0 }}
                  exit={{ opacity:0, x:16 }} transition={{ duration:0.3 }}
                  className="flex flex-col gap-3">
                  {dayMealsList.length > 0 ? (
                    dayMealsList.map((m, mi) => {
                      const isChecked = checkedMeals[activeDay]?.has(mi) || false;
                      return (
                        <MealCard
                          key={m.id || mi}
                          slot={{
                            id: m.mealSlot || "meal",
                            label: m.mealSlot || "Meal",
                            icon: (m.mealSlot || "").toLowerCase().includes("breakfast") ? FiSunrise : (m.mealSlot || "").toLowerCase().includes("lunch") ? FiSun : (m.mealSlot || "").toLowerCase().includes("dinner") ? FiMoon : FiCoffee,
                            time: m.time || "08:00 AM",
                            accent: mi % 2 === 0 ? T.orange : T.olive,
                            bg: mi % 2 === 0 ? "rgba(255,244,232,0.75)" : "rgba(245,250,242,0.75)",
                            border: mi % 2 === 0 ? "rgba(242,154,12,0.18)" : "rgba(107,138,90,0.18)"
                          }}
                          meal={{
                            name: m.mealName,
                            description: m.description || m.Description || m.notes || m.Notes || "",
                            cal: m.calories,
                            protein: m.protein,
                            carbs: m.carbs,
                            fat: m.fat,
                            prep: m.prepTime || "15 min",
                            notes: m.description || m.Description || m.notes || m.Notes || "",
                            tags: m.tags ? (Array.isArray(m.tags) ? m.tags : m.tags.split(",")) : [dbPlan?.dietType || userPref?.dietType || "Veg", dbPlan?.region || userPref?.region || "Indian"]
                          }}
                          checked={isChecked}
                          onToggle={() => toggleMeal(activeDay, mi)}
                          onUpgrade={setUpgradeModal}
                          userTierRank={getTierRank(userTier)}
                          delay={mi * 0.05}
                        />
                      );
                    })
                  ) : (
                    <div className="text-center py-12 px-6 rounded-[24px] border"
                      style={{ background:"rgba(255,255,255,0.85)", borderColor:T.border }}>
                      {aiGenerationEnabled ? (
                        <FiZap size={36} className="mx-auto mb-3 text-amber-500 animate-pulse"/>
                      ) : (
                        <FiFramer size={36} className="mx-auto mb-3 text-amber-600/70"/>
                      )}
                      <h3 className="font-bold text-[18px] text-[#1F1F1F]">No Plan Active for {userPref?.dietType || 'Veg'} · {userPref?.region || 'Indian'}</h3>
                      <p className="text-[13px] text-gray-600 mt-1 max-w-md mx-auto leading-relaxed">
                        {aiGenerationEnabled
                          ? "Generate your own personalized 7-day nutritional schedule tailored directly to your fitness goals and allergies in seconds with AI."
                          : customisePrefsEnabled
                          ? "No published plan found for your selected category. You can update your dietary preferences to view available published plans."
                          : `No published meal plan is currently available for this category under ${userTier} Tier. Please check back later or contact your instructor.`}
                      </p>
                      {(aiGenerationEnabled || customisePrefsEnabled) && (
                        <div className="flex justify-center gap-3 mt-5">
                          {aiGenerationEnabled && (
                            <button type="button" onClick={() => setShowPrefModal(true)}
                              className="px-6 py-2.5 rounded-full text-white text-[13px] font-extrabold border-none cursor-pointer shadow-lg hover:opacity-95 transition-all"
                              style={{ background: `linear-gradient(135deg, ${T.orange}, ${T.orange2})` }}>
                              ✨ Generate 7-Day Plan with AI Now
                            </button>
                          )}
                          {customisePrefsEnabled && (
                            <button type="button" onClick={() => setShowPrefModal(true)}
                              className="px-5 py-2.5 rounded-full text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 cursor-pointer hover:bg-zinc-50 transition-colors">
                              Change Preferences
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Right panel */}
            <div className="flex flex-col gap-4">
              {/* Macro progress */}
              <TiltCard intensity={4} className="rounded-[28px] p-6" style={glass}>
                <p className="font-black text-[15px] text-[#1F1F1F] mb-1" style={{ fontFamily:"'Poppins',sans-serif" }}>
                  {FULL_DAYS[activeDay].substring(0,3)}'s Nutrition
                </p>
                <p className="text-[11px] mb-5" style={{ color:T.muted }}>{todayTotals.cal > 0 ? `${todayTotals.cal} kcal consumed` : "No meals logged yet"}</p>
                {macros.map((m) => (
                  <div key={m.label} className="mb-4">
                    <div className="flex justify-between text-[11px] mb-1.5">
                      <span className="font-semibold" style={{ color:T.mid }}>{m.label}</span>
                      <span className="font-black" style={{ color:m.color.includes("linear") ? T.orange : m.color }}>{m.val} / {m.max} {m.unit}</span>
                    </div>
                    <ProgressBar value={m.val} max={m.max} color={m.color} track={m.track} />
                  </div>
                ))}
                <div className="rounded-[14px] p-3 flex items-start gap-2.5 mt-2"
                  style={{ background:"rgba(107,138,90,0.08)" }}>
                  <FiInfo size={14} style={{ color:T.olive, flexShrink:0, marginTop:1 }} />
                  <p className="text-[11px] leading-[1.55]" style={{ color:T.mid }}>
                    Eat 30 min before yoga for energy. Avoid heavy meals within 2h of your session.
                  </p>
                </div>
              </TiltCard>

              {/* Weekly adherence heatmap */}
              <TiltCard intensity={4} className="rounded-[28px] p-6" style={glass}>
                <p className="font-black text-[15px] text-[#1F1F1F] mb-4" style={{ fontFamily:"'Poppins',sans-serif" }}>Week Adherence</p>
                <div className="grid grid-cols-7 gap-1.5 mb-3">
                  {DAYS.map((d, i) => {
                    const cnt = checkedMeals[i]?.size || 0;
                    const daySpecificCount = dbPlan?.meals?.filter(m => m.day === FULL_DAYS[i])?.length || 0;
                    const pct = daySpecificCount > 0 ? cnt / daySpecificCount : 0;
                    return (
                      <div key={d} className="text-center">
                        <motion.div
                          initial={{ scale: 0 }} animate={{ scale: 1 }}
                          transition={{ delay: i * 0.05, type: "spring", stiffness: 250 }}
                          className="aspect-square rounded-[8px]"
                          style={{
                            background: pct >= 1 ? `linear-gradient(135deg,${T.olive},${T.olive2})`
                              : pct >= 0.5 ? `linear-gradient(135deg,${T.orange},${T.orange2})`
                              : pct > 0 ? "rgba(242,154,12,0.3)"
                              : "rgba(239,229,216,0.5)",
                            boxShadow: pct >= 1 ? `0 3px 8px rgba(107,138,90,0.35)` : "none",
                          }} />
                        <p className="text-[9px] font-bold mt-1" style={{ color: i === activeDay ? T.orange : T.light }}>{d}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {[["Complete", `linear-gradient(135deg,${T.olive},${T.olive2})`], ["Partial", `linear-gradient(135deg,${T.orange},${T.orange2})`], ["None", "rgba(239,229,216,0.5)"]].map(([l, bg]) => (
                    <div key={l} className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-[3px]" style={{ background: bg }} />
                      <span className="text-[10px]" style={{ color: T.light }}>{l}</span>
                    </div>
                  ))}
                </div>
              </TiltCard>

              {/* Water tracker (Initial state starts at 0 filled glasses) */}
              <TiltCard intensity={4} className="rounded-[28px] p-6" style={glass}>
                <div className="flex items-center justify-between mb-4">
                  <p className="font-black text-[15px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>Water Intake</p>
                  <span className="font-black text-[12px]" style={{ color:"#185FA5" }}>{waterGlasses} / 12 glasses</span>
                </div>
                <div className="grid grid-cols-6 gap-1.5 mb-4">
                  {Array.from({ length: 12 }, (_, i) => (
                    <motion.div key={i}
                      whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                      onClick={() => handleUpdateWater(i < waterGlasses ? i : i + 1)}
                      className="aspect-square rounded-[8px] cursor-pointer"
                      style={{
                        background: i < waterGlasses ? "linear-gradient(180deg,#85B7EB,#378ADD)" : "rgba(239,229,216,0.4)",
                        boxShadow: i < waterGlasses ? "0 2px 6px rgba(55,138,221,0.3)" : "none",
                      }} />
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[11px]" style={{ color:T.light }}>Each glass = 250ml</span>
                  <motion.button whileHover={{ y:-1 }} onClick={() => handleUpdateWater(waterGlasses + 1)}
                    className="h-8 px-4 rounded-full text-[11px] font-bold flex items-center gap-1.5"
                    style={{ background:"rgba(55,138,221,0.1)", border:"1px solid rgba(55,138,221,0.2)", color:"#185FA5", cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                    <FiPlus size={11} /> Add Glass
                  </motion.button>
                </div>
              </TiltCard>
            </div>
          </motion.div>
          </>
          )}
        </div>
      </div>

      {/* ── MODALS ── */}
      <AnimatePresence>
        {upgradeModal && (
          <UpgradeModal
            mealLabel={typeof upgradeModal === "string" ? upgradeModal : null}
            title={upgradeModal?.title}
            description={upgradeModal?.description}
            onClose={() => setUpgradeModal(null)}
          />
        )}
        {quotaModal && (
          <QuotaModal
            quotaInfo={quotaModal}
            onRedeemPoints={() => handleGenerateMyDietPlan(quotaModal.pendingPref, true)}
            onClose={() => setQuotaModal(null)}
          />
        )}
        {dayCompleteModal && <DayCompleteModal dayName={dayCompleteModal} onClose={() => setDayCompleteModal(null)} />}
        {showPrefModal && (
          <PreferenceModal
            initialPref={userPref}
            userProfile={userProfile}
            options={dynamicOptions}
            quota={quota}
            onClose={() => setShowPrefModal(false)}
            generating={generatingAi}
            aiGenerationEnabled={aiGenerationEnabled}
            onGenerate={(newPref) => handleGenerateMyDietPlan(newPref, false)}
            onSave={(newPref) => {
              setUserPref(newPref);
              fetchDietData(newPref);
            }}
          />
        )}
        {errorModal && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            onClick={() => setErrorModal(null)}>
            <motion.div initial={{ scale:0.92, opacity:0 }} animate={{ scale:1, opacity:1 }} exit={{ scale:0.92, opacity:0 }}
              className="bg-white rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl border border-zinc-100 text-center"
              onClick={e => e.stopPropagation()}>
              <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-4">
                <FiAlertTriangle size={24} />
              </div>
              <h3 className="text-lg font-black text-zinc-900 mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                AI Generation Notice
              </h3>
              <p className="text-xs text-zinc-600 leading-relaxed mb-6">
                {errorModal}
              </p>
              <button
                type="button"
                onClick={() => setErrorModal(null)}
                className="w-full py-2.5 rounded-full text-white font-bold text-xs bg-zinc-900 hover:bg-zinc-800 border-none cursor-pointer transition-colors shadow-md"
              >
                Got It
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </UserDashboardLayout>
  );
}